/**
 * tableLook.js - how a customizable table draws a view's look (round 4,
 * 10/9/26, the Customize revamp): density, the table's font size, zebra
 * rows, gridlines, the header style, wrap vs "…", where the totals show,
 * the sticky header, column alignment and frozen columns. The classes live
 * in index.css (.tv-*), so a change restyles the table without touching its
 * rows. Shared by ProjectsView and OpportunitiesTable.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { cleanLook } from './viewConfig';

/** The table element's classes and style, and the switches the rows need. PURE. */
export function lookOf(view) {
  const l = cleanLook(view && view.look);
  return {
    ...l,
    tableClass: [
      l.density !== 'compact' ? `tv-density-${l.density}` : '',
      l.gridlines ? '' : 'tv-nogrid',
      l.header === 'light' ? 'tv-head-light' : '',
      l.wrap ? '' : 'tv-truncate',
    ]
      .filter(Boolean)
      .join(' '),
    tableStyle: l.fontSize ? { fontSize: l.fontSize } : {},
    totalsTop: l.totals === 'both' || l.totals === 'top',
    totalsBottom: l.totals === 'both' || l.totals === 'bottom',
  };
}

/** A column's alignment as a cell style ({ textAlign }) or undefined. */
export const alignOf = (view, col) => {
  const a = view && view.align && view.align[col];
  return a ? { textAlign: a } : undefined;
};

/**
 * Where each frozen column sticks: { colId: left px } for the pinned columns
 * on screen, read off the header row after it is drawn (with saved widths it
 * is simply their sum). Re-measured when the columns, widths or sizes change.
 * `last` is the last frozen column (it carries the edge shadow).
 */
export function usePinnedLefts(view, widthOf) {
  const rowRef = useRef(null);
  const pinned = (view.visible || []).filter((c) => (view.pinned || []).includes(c.col)).map((c) => c.col);
  const key = `${pinned.join(',')}|${(view.visible || []).length}|${JSON.stringify(view.widths || {})}|${JSON.stringify(view.fonts || {})}|${JSON.stringify(view.look || {})}`;
  const [lefts, setLefts] = useState({});
  useLayoutEffect(() => {
    if (!pinned.length) {
      setLefts((was) => (Object.keys(was).length ? {} : was));
      return;
    }
    const next = {};
    let x = 0;
    for (const col of pinned) {
      next[col] = x;
      const known = widthOf && widthOf(col);
      const th = rowRef.current && rowRef.current.querySelector(`th[data-col="${col}"]`);
      x += known || (th ? Math.round(th.getBoundingClientRect().width) : 0);
    }
    setLefts((was) => (JSON.stringify(was) === JSON.stringify(next) ? was : next));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { rowRef, lefts, last: pinned[pinned.length - 1] || null, isPinned: (col) => pinned.includes(col) };
}

/** The sticky-left style for a frozen cell (td), or undefined. */
export const pinStyle = (pins, col) => (pins.isPinned(col) ? { position: 'sticky', left: pins.lefts[col] || 0, zIndex: 2 } : undefined);

/**
 * The Actions column's width: one "Close as..." dropdown (11/3/26, it replaced
 * the Won / Lost / Cancelled buttons) when any close action is offered, so
 * saved widths never clip it. PURE.
 */
export function actionsWidth({ won = true, lost = true, cancelled = true, noQuote = true } = {}) {
  return won || lost || cancelled || noQuote ? 104 : 40;
}
